import { deferLayoutWithRaf } from '@thoughtspot/session-service';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizeAnchorElement } from '../_utils';
import { getTooltipProps } from '../typography/typography-utils';
import { escapeAccidentalComments } from './accidental-comments';
/**
 * SafeHTML is a thin wrapper over React's dangerouslySetInnerHTML.
 * It sanitizes the input HTML string using dompurify's sanitize function.
 *
 * Example:
 *
 * - Use this:
 *
 * ```
 * <SafeHTML html={myHTMLString} />
 * ```
 *
 * - Don't use this:
 *
 * ```
 * <div dangerouslySetInnerHTML={{ __html: myHTMLString }} />
 * ```
 */
export const SafeHTML = ({ html, className, config, tooltipConfig, hyperLinkSanitization = false, ...restProps }) => {
    const ref = useRef(null);
    const [isEllipsisShowing, setIsEllipsisShowing] = useState(false);
    const deferLayoutWithRafEnabled = deferLayoutWithRaf === null || deferLayoutWithRaf === void 0 ? void 0 : deferLayoutWithRaf();
    const rafIdRef = useRef(0);
    const { tooltipProps, sanitizedHTMLString } = React.useMemo(() => {
        let sanitizedHTML;
        // Create an isolated DOMPurify instance to avoid affecting concurrent renders
        const purify = DOMPurify();
        // Escape accidental comments so the sanitizer doesn't hide part of the text.
        const htmlToSanitize = escapeAccidentalComments(html);
        // Check if any custom sanitization is enabled
        const hasCustomSanitization = hyperLinkSanitization;
        // Future: extend with more flags, e.g.:
        // const hasCustomSanitization = hyperLinkSanitization || imageSanitization || ...;
        if (hasCustomSanitization) {
            purify.addHook('afterSanitizeAttributes', (node) => {
                // Sanitize anchor elements to prevent open redirect vulnerabilities
                if (hyperLinkSanitization && (node === null || node === void 0 ? void 0 : node.nodeName) === 'A') {
                    sanitizeAnchorElement(node);
                }
                // Future: add more element-specific sanitizations here
            });
        }
        if (config) {
            sanitizedHTML = purify.sanitize(htmlToSanitize, {
                ...config,
                RETURN_DOM_FRAGMENT: false,
                RETURN_DOM: false,
            });
        }
        else {
            sanitizedHTML = purify.sanitize(htmlToSanitize);
        }
        return {
            tooltipProps: getTooltipProps(sanitizedHTML || html, tooltipConfig, isEllipsisShowing),
            sanitizedHTMLString: sanitizedHTML,
        };
    }, [isEllipsisShowing, config, tooltipConfig, html, hyperLinkSanitization]);
    const applyEllipsisCheck = useCallback((el) => {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => setIsEllipsisShowing(el.clientHeight < el.scrollHeight ||
            el.clientWidth < el.scrollWidth));
    }, []);
    useEffect(() => {
        if (!deferLayoutWithRafEnabled)
            return undefined;
        const el = ref.current;
        if (!el)
            return undefined;
        const observer = new ResizeObserver(() => applyEllipsisCheck(el));
        observer.observe(el);
        return () => {
            observer.disconnect();
            cancelAnimationFrame(rafIdRef.current);
        };
    }, [deferLayoutWithRafEnabled, applyEllipsisCheck]);
    // Re-check ellipsis when content changes (e.g. async translation resolves).
    // ResizeObserver only fires on box-dimension changes, not scrollWidth changes
    // caused by different text content inside a fixed-width container.
    useEffect(() => {
        if (!deferLayoutWithRafEnabled)
            return undefined;
        const el = ref.current;
        if (!el)
            return undefined;
        applyEllipsisCheck(el);
        return () => cancelAnimationFrame(rafIdRef.current);
    }, [deferLayoutWithRafEnabled, sanitizedHTMLString, applyEllipsisCheck]);
    useEffect(() => {
        if (deferLayoutWithRafEnabled)
            return;
        if (ref.current &&
            (ref.current.clientHeight < ref.current.scrollHeight ||
                ref.current.clientWidth < ref.current.scrollWidth)) {
            setIsEllipsisShowing(true);
        }
        else {
            setIsEllipsisShowing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref === null || ref === void 0 ? void 0 : ref.current, deferLayoutWithRafEnabled]);
    // When DOMPurify.sanitize(html) fails due to invalid html, it return empty string.
    // So, to handle that behaviour we need to return the value as a string itself
    return sanitizedHTMLString ? (React.createElement("div", { className: className, ref: ref, 
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML: { __html: sanitizedHTMLString }, ...tooltipProps, ...restProps })) : (React.createElement("div", { className: className, ref: ref, ...tooltipProps, ...restProps }, html));
};
/**
 *
 * @param text text to check
 * @returns it returns boolean  on the basis of given test has HTML Tags present or not
 */
export const isHTML = (text) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};
// True when the HTML would render nothing visible (no text, no image).
export const isHTMLEmpty = (html) => {
    var _a;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const hasText = ((_a = doc.body.textContent) !== null && _a !== void 0 ? _a : '').trim().length > 0;
    const hasImage = doc.body.querySelector('img') !== null;
    return !hasText && !hasImage;
};
// Strings without `<` or `&` cannot contain markup or entities, so return them as-is.
const MAY_CONTAIN_MARKUP = /[<&]/;
/**
 * Converts rendered HTML to the plain text shown by <SafeHTML>
 *
 * Removes HTML tags, turns HTML-encoded characters into the characters they represent (&amp; into &),
 * and leaves out content dropped by the sanitizer, such as scripts and styles. Text inside accidental comments is
 * kept, as <SafeHTML> shows it.
 * If there is no HTML to parse, returns the input unchanged. If nothing is left after parsing,
 * the original input is returned.
 */
export const getPlainTextFromHtml = (html) => {
    var _a;
    if (!MAY_CONTAIN_MARKUP.test(html)) {
        return html;
    }
    const body = DOMPurify.sanitize(escapeAccidentalComments(html), {
        RETURN_DOM: true,
    });
    if (!body.hasChildNodes()) {
        return html;
    }
    return (_a = body.textContent) !== null && _a !== void 0 ? _a : '';
};
//# sourceMappingURL=safe-html.js.map